import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useHelpVisibility, HelpButton } from '../hooks/useHelpVisibility';

// Official Bananagrams 144-tile letter distribution.
const LETTER_DISTRIBUTION = {
  A: 13, B: 3, C: 3, D: 6, E: 18, F: 3, G: 4, H: 3, I: 12, J: 2,
  K: 2, L: 5, M: 3, N: 8, O: 11, P: 3, Q: 2, R: 9, S: 6, T: 9,
  U: 6, V: 3, W: 3, X: 2, Y: 3, Z: 2,
};

const MAX_TILES = 50;
const TIME_BUDGET_MS = 30000;

// Build the full 144-tile bag for random draws.
function buildBag() {
  const bag = [];
  for (const [letter, count] of Object.entries(LETTER_DISTRIBUTION)) {
    for (let i = 0; i < count; i++) bag.push(letter);
  }
  return bag;
}

// Draw n random tiles from a fresh bag (without replacement).
function drawTiles(n) {
  const bag = buildBag();
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return bag.slice(0, n).join('');
}

// A single Scrabble-style tile square.
const Tile = ({ letter, className = '' }) => (
  <div
    className={`flex items-center justify-center rounded-md bg-amber-100 text-gray-900 font-bold shadow-md ${className}`}
  >
    {letter.toUpperCase()}
  </div>
);

const Banagrams = () => {
  const [rawTiles, setRawTiles] = useState('');
  const [minWordLen, setMinWordLen] = useState(2);
  // 0 means the longest-word constraint is off.
  const [longestWordMin, setLongestWordMin] = useState(0);
  // 0 means the minimum-word-count constraint is off.
  const [minWords, setMinWords] = useState(0);
  // Which dictionary tier the solver may draw words from.
  const [wordlist, setWordlist] = useState('standard');
  // Spellings are Australian by default; ticking this re-admits US-only
  // spellings (color, organize...).
  const [usSpellings, setUsSpellings] = useState(false);
  const [solving, setSolving] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [hasSolved, setHasSolved] = useState(false);
  const [error, setError] = useState(null);

  // Fleet of parallel solver workers racing on different seeds; first
  // solution wins.
  const workersRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  const { showHelp, toggleHelp } = useHelpVisibility();

  // Normalise the raw input into an uppercase list of tiles.
  const tiles = useMemo(
    () => rawTiles.toUpperCase().replace(/[^A-Z]/g, '').slice(0, MAX_TILES).split(''),
    [rawTiles]
  );

  const killWorkers = useCallback(() => {
    for (const w of workersRef.current) w.terminate();
    workersRef.current = [];
  }, []);

  // Cleanup workers + timer on unmount.
  useEffect(() => {
    return () => {
      for (const w of workersRef.current) w.terminate();
      workersRef.current = [];
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleInputChange = (e) => {
    const cleaned = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, MAX_TILES);
    setRawTiles(cleaned);
  };

  const handleDraw21 = () => {
    setRawTiles(drawTiles(21));
    setResult(null);
    setHasSolved(false);
    setError(null);
  };

  const handleClear = () => {
    setRawTiles('');
    setResult(null);
    setHasSolved(false);
    setError(null);
  };

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleStop = useCallback(() => {
    killWorkers();
    stopTimer();
    setSolving(false);
  }, [killWorkers, stopTimer]);

  // Counts solves of the current rack: passed to the solver as seedOffset
  // so "Solve Again" starts from a different opening word.
  const solveCountRef = useRef(0);

  const handleSolve = useCallback(() => {
    if (tiles.length < 2 || solving) return;

    setSolving(true);
    setResult(null);
    setHasSolved(false);
    setProgress(null);
    setError(null);
    setElapsedMs(0);

    startTimeRef.current = Date.now();
    stopTimer();
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 100);

    // Race a fleet of workers on different seeds/openers; first solution
    // wins and terminates the rest.
    killWorkers();
    const fleetSize = Math.min(6, Math.max(2, (navigator.hardwareConcurrency || 4) - 1));
    const round = solveCountRef.current;
    const baseSeed = Date.now() & 0xffffff;
    const perWorkerProgress = new Array(fleetSize).fill(null);
    let pending = fleetSize;
    let finished = false;
    let firstErrorMessage = null;

    const finish = (solution) => {
      if (finished) return;
      finished = true;
      killWorkers();
      stopTimer();
      setElapsedMs(Date.now() - startTimeRef.current);
      if (solution) {
        setResult(solution);
        setHasSolved(true);
      } else if (firstErrorMessage && pending === 0) {
        setError(firstErrorMessage);
      } else {
        setResult(null);
        setHasSolved(true);
      }
      setSolving(false);
    };

    for (let i = 0; i < fleetSize; i++) {
      const worker = new Worker('/banagrams-worker.js');
      workersRef.current.push(worker);
      const workerIndex = i;

      worker.onmessage = (e) => {
        const msg = e.data || {};
        if (msg.type === 'progress') {
          const { type, ...info } = msg;
          perWorkerProgress[workerIndex] = info;
          // Aggregate: total restarts across the fleet.
          const restarts = perWorkerProgress.reduce((s, p) => s + ((p && p.restarts) || 0), 0);
          setProgress({ restarts, workers: fleetSize });
        } else if (msg.type === 'result') {
          pending -= 1;
          if (msg.solution) {
            finish(msg.solution);
          } else if (pending === 0) {
            finish(null); // every worker exhausted its budget
          }
        } else if (msg.type === 'error') {
          pending -= 1;
          firstErrorMessage = firstErrorMessage || msg.message || 'The solver ran into an error.';
          if (pending === 0) finish(null);
        }
      };

      worker.onerror = (err) => {
        pending -= 1;
        firstErrorMessage = firstErrorMessage || err.message || 'The solver worker failed to start.';
        if (pending === 0) finish(null);
      };

      worker.postMessage({
        type: 'solve',
        tiles: tiles.map((t) => t.toLowerCase()),
        minWordLen,
        longestWordMin,
        minWords,
        wordlist,
        usSpellings,
        timeBudgetMs: TIME_BUDGET_MS,
        // Distinct opener rotation and RNG stream per worker, shifted each
        // "Solve Again" round.
        seedOffset: round * fleetSize + workerIndex,
        seed: baseSeed + workerIndex * 7919,
      });
    }
    solveCountRef.current += 1;
  }, [tiles, solving, minWordLen, longestWordMin, minWords, wordlist, usSpellings, stopTimer, killWorkers]);

  // A new rack starts the opening-word rotation over.
  useEffect(() => {
    solveCountRef.current = 0;
  }, [tiles]);

  const formatSeconds = (ms) => `${(ms / 1000).toFixed(1)}s`;

  // Derive the list of placed cells from whichever field the solution provides.
  const solutionCells = useMemo(() => {
    if (!result) return [];
    if (Array.isArray(result.cells)) {
      return result.cells
        .filter((c) => c && typeof c.r === 'number' && typeof c.c === 'number' && c.letter)
        .map((c) => ({ r: c.r, c: c.c, letter: c.letter }));
    }
    if (result.grid && typeof result.grid === 'object') {
      return Object.entries(result.grid).map(([key, letter]) => {
        const [r, c] = key.split(',').map(Number);
        return { r, c, letter };
      });
    }
    return [];
  }, [result]);

  // Compute bounding box for the board grid.
  const bounds = useMemo(() => {
    if (solutionCells.length === 0) return null;
    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    for (const { r, c } of solutionCells) {
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
    }
    return { minR, maxR, minC, maxC, rows: maxR - minR + 1, cols: maxC - minC + 1 };
  }, [solutionCells]);

  // Map from "r,c" -> letter for quick grid lookup.
  const cellMap = useMemo(() => {
    const map = new Map();
    for (const { r, c, letter } of solutionCells) {
      map.set(`${r},${c}`, letter);
    }
    return map;
  }, [solutionCells]);

  const words = useMemo(() => {
    if (!result || !Array.isArray(result.words)) return [];
    return [...result.words]
      .filter((w) => w && w.word)
      .sort((a, b) => b.word.length - a.word.length);
  }, [result]);

  const canSolve = tiles.length >= 2 && !solving;

  // Word definitions: hover/tap a word chip to look it up (cached).
  const [activeWord, setActiveWord] = useState(null);
  const [definitions, setDefinitions] = useState({});
  const defRequestedRef = useRef(new Set());

  const lookupDefinition = useCallback((word) => {
    if (defRequestedRef.current.has(word)) return;
    defRequestedRef.current.add(word);
    setDefinitions((d) => ({ ...d, [word]: { status: 'loading' } }));
    fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('not found'))))
      .then((json) => {
        const entries = [];
        for (const meaning of (json[0] && json[0].meanings) || []) {
          const def = meaning.definitions && meaning.definitions[0];
          if (def && def.definition) {
            entries.push({ pos: meaning.partOfSpeech, def: def.definition });
          }
          if (entries.length >= 2) break;
        }
        setDefinitions((d) => ({
          ...d,
          [word]: entries.length ? { status: 'ok', entries } : { status: 'none' },
        }));
      })
      .catch(() => {
        setDefinitions((d) => ({ ...d, [word]: { status: 'none' } }));
      });
  }, []);

  const handleWordHover = useCallback((word) => {
    setActiveWord(word);
    lookupDefinition(word);
  }, [lookupDefinition]);

  const handleWordTap = useCallback((word) => {
    setActiveWord((current) => (current === word ? null : word));
    lookupDefinition(word);
  }, [lookupDefinition]);

  const activeDefinition = activeWord && words.some((w) => w.word === activeWord)
    ? definitions[activeWord]
    : null;

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-4 mb-2">
        <h1 className="text-3xl font-bold">Banagrams Solver</h1>
        <HelpButton onClick={toggleHelp} isActive={showHelp} />
      </div>
      <p className="text-gray-400 mb-6">Turn a hand of tiles into a connected crossword grid</p>

      {/* Help section */}
      {showHelp && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-surface rounded-lg max-w-xl text-gray-400 text-sm"
        >
          <h3 className="text-white font-semibold mb-2">How it works</h3>
          <p className="mb-2">
            Bananagrams is a fast word game where you race to arrange all your letter tiles into a
            single connected crossword grid, with every run of tiles forming a valid word. Enter
            your tiles (or draw a random 21-tile hand), tune the constraints, and let the solver
            find an arrangement that uses your letters.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <span className="text-white font-medium">Dictionary</span> — how permissive the word
              list is: Everyday for words anyone would accept, Standard for real dictionary words,
              Tournament for the full Scrabble-legal list. Australian spellings by default; tick
              US spellings to allow American forms too.
            </li>
            <li>
              <span className="text-white font-medium">Min word length</span> — the shortest word
              allowed anywhere on the board. Raise it to avoid tiny two-letter words.
            </li>
            <li>
              <span className="text-white font-medium">Longest word</span> — at least one word on
              the board must be at least this long, forcing a meatier arrangement. Set it to Off
              to accept any valid arrangement.
            </li>
            <li>
              <span className="text-white font-medium">Min words</span> — the board must contain at
              least this many words, forcing the tiles to spread out instead of forming one or two
              long words. Set it to Off for no requirement.
            </li>
          </ul>
        </motion.div>
      )}

      {/* Tile input */}
      <div className="w-full max-w-xl bg-surface rounded-lg p-5 mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Your tiles
        </label>
        <input
          type="text"
          value={rawTiles}
          onChange={handleInputChange}
          placeholder="Type your letters (A–Z)"
          maxLength={MAX_TILES}
          className="w-full px-3 py-2 rounded bg-background border border-gray-600 text-white uppercase tracking-widest focus:outline-none focus:border-primary"
        />

        <div className="mt-3 flex items-center justify-between">
          <span className="text-sm text-gray-400">
            {tiles.length} / {MAX_TILES} tiles
          </span>
          <div className="flex gap-2">
            <button onClick={handleDraw21} className="btn btn-primary text-sm">
              Draw 21
            </button>
            <button
              onClick={handleClear}
              className="btn bg-gray-600 hover:bg-gray-500 text-sm"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Live tile preview */}
        {tiles.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {tiles.map((letter, i) => (
              <Tile key={i} letter={letter} className="w-9 h-9 text-lg" />
            ))}
          </div>
        )}
      </div>

      {/* Sliders */}
      <div className="w-full max-w-xl bg-surface rounded-lg p-5 mb-6 space-y-6">
        {/* Dictionary tier */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="banagrams-wordlist" className="text-sm font-medium text-gray-300">
              Dictionary
            </label>
          </div>
          <select
            id="banagrams-wordlist"
            value={wordlist}
            onChange={(e) => setWordlist(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white cursor-pointer focus:outline-none focus:border-primary"
          >
            <option value="common">Everyday (~30k words — dinner-table friendly)</option>
            <option value="standard">Standard (~80k words — real dictionary words)</option>
            <option value="tournament">Tournament (~173k words — anything Scrabble-legal)</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">
            {wordlist === 'common'
              ? 'Only common words everyone at the table will recognise.'
              : wordlist === 'standard'
                ? 'Curated dictionary words (12dicts) — no obscure tournament jargon.'
                : 'The full ENABLE tournament list, including obscure but legal words.'}
          </p>
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={usSpellings}
              onChange={(e) => setUsSpellings(e.target.checked)}
              className="accent-primary w-4 h-4 cursor-pointer"
            />
            US spellings
          </label>
          <p className="text-xs text-gray-400 mt-1">
            {usSpellings
              ? 'US-only spellings (color, organize) are allowed alongside Australian ones.'
              : 'Australian spellings only — colour, organise; US-only forms are excluded.'}
          </p>
        </div>

        {/* Min word length */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-300">Min word length</label>
            <span className="text-primary font-bold text-lg">{minWordLen}</span>
          </div>
          <input
            type="range"
            min={2}
            max={4}
            step={1}
            value={minWordLen}
            onChange={(e) => setMinWordLen(Number(e.target.value))}
            className="w-full accent-primary cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1 px-0.5">
            {[2, 3, 4].map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">Shortest word allowed on the board.</p>
        </div>

        {/* Longest word */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-300">Longest word</label>
            <span className="text-primary font-bold text-lg">
              {longestWordMin === 0 ? 'Off' : longestWordMin}
            </span>
          </div>
          <input
            type="range"
            min={5}
            max={9}
            step={1}
            value={longestWordMin === 0 ? 5 : longestWordMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              setLongestWordMin(v === 5 ? 0 : v);
            }}
            className="w-full accent-primary cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1 px-0.5">
            {['Off', 6, 7, 8, 9].map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {longestWordMin === 0
              ? 'No length requirement — any valid arrangement is accepted.'
              : 'At least one word on the board must be at least this long.'}
          </p>
        </div>

        {/* Min number of words */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-300">Min words</label>
            <span className="text-primary font-bold text-lg">
              {minWords === 0 ? 'Off' : minWords}
            </span>
          </div>
          <input
            type="range"
            min={1}
            max={8}
            step={1}
            value={minWords === 0 ? 1 : minWords}
            onChange={(e) => {
              const v = Number(e.target.value);
              setMinWords(v === 1 ? 0 : v);
            }}
            className="w-full accent-primary cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1 px-0.5">
            {['Off', 2, 3, 4, 5, 6, 7, 8].map((v) => (
              <span key={v}>{v}</span>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {minWords === 0
              ? 'No word-count requirement.'
              : 'The board must contain at least this many words.'}
          </p>
        </div>
      </div>

      {/* Solve / Stop controls */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={handleSolve}
          disabled={!canSolve}
          className={`btn text-lg px-8 ${
            canSolve ? 'btn-primary' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          {hasSolved ? 'Solve Again' : 'Solve'}
        </button>
        {solving && (
          <button onClick={handleStop} className="btn bg-red-500 hover:bg-red-600 text-lg px-6">
            Stop
          </button>
        )}
      </div>

      {tiles.length < 2 && (
        <p className="text-gray-500 text-sm mb-6 -mt-2">Enter at least 2 tiles to solve.</p>
      )}

      {/* Solving progress */}
      {solving && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="w-full max-w-xl bg-surface rounded-lg p-5 mb-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
            <span className="font-semibold">Solving…</span>
            <span className="ml-auto font-mono text-gray-300">{formatSeconds(elapsedMs)}</span>
          </div>
          {progress && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-400">
              {typeof progress.placed === 'number' && (
                <span>
                  Placed:{' '}
                  <span className="text-white font-mono">
                    {progress.placed}
                    {typeof progress.total === 'number' ? ` / ${progress.total}` : ''}
                  </span>
                </span>
              )}
              {typeof progress.restarts === 'number' && (
                <span>
                  Restarts: <span className="text-white font-mono">{progress.restarts}</span>
                </span>
              )}
              {typeof progress.bestTilesUsed === 'number' && (
                <span>
                  Best so far:{' '}
                  <span className="text-white font-mono">{progress.bestTilesUsed} tiles</span>
                </span>
              )}
              {typeof progress.workers === 'number' && (
                <span>
                  Workers: <span className="text-white font-mono">{progress.workers}</span>
                </span>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Error */}
      {error && !solving && (
        <div className="w-full max-w-xl mb-6 p-4 bg-red-600/80 rounded-lg text-white text-sm">
          {error}
        </div>
      )}

      {/* No-solution message */}
      {hasSolved && !error && !result && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-xl mb-6 p-4 bg-surface border border-gray-600 rounded-lg text-gray-300 text-center"
        >
          No valid arrangement found within the time limit — try relaxing the constraints.
        </motion.div>
      )}

      {/* Result board */}
      {result && bounds && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-3xl mb-6"
        >
          <h2 className="text-xl font-bold mb-3 text-center">Solution</h2>

          <div className="bg-gray-800 rounded-lg p-4 overflow-x-auto">
            <div
              className="grid gap-1 mx-auto w-max"
              style={{
                gridTemplateColumns: `repeat(${bounds.cols}, 2.25rem)`,
                gridTemplateRows: `repeat(${bounds.rows}, 2.25rem)`,
              }}
            >
              {Array.from({ length: bounds.rows }).map((_, ri) =>
                Array.from({ length: bounds.cols }).map((_, ci) => {
                  const r = bounds.minR + ri;
                  const c = bounds.minC + ci;
                  const letter = cellMap.get(`${r},${c}`);
                  if (!letter) {
                    return <div key={`${r},${c}`} className="w-9 h-9" />;
                  }
                  const idx = ri * bounds.cols + ci;
                  return (
                    <motion.div
                      key={`${r},${c}`}
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: Math.min(idx * 0.015, 1.2), duration: 0.2 }}
                    >
                      <Tile letter={letter} className="w-9 h-9 text-lg" />
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="mt-4 flex flex-wrap justify-center gap-6 text-sm">
            <div className="text-gray-400">
              Time taken:{' '}
              <span className="text-white font-mono">
                {formatSeconds(result.stats?.elapsedMs ?? elapsedMs)}
              </span>
            </div>
            <div className="text-gray-400">
              Words: <span className="text-white font-mono">{words.length}</span>
            </div>
            {typeof result.stats?.restarts === 'number' && (
              <div className="text-gray-400">
                Restarts: <span className="text-white font-mono">{result.stats.restarts}</span>
              </div>
            )}
          </div>

          {/* Word list */}
          {words.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-300 mb-2 text-center">
                Words used (longest first)
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                {words.map((w, i) => (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => handleWordHover(w.word)}
                    onClick={() => handleWordTap(w.word)}
                    className={`px-3 py-1 rounded-full text-sm font-medium uppercase tracking-wide cursor-pointer transition-colors ${
                      activeWord === w.word
                        ? 'bg-amber-100 text-gray-900'
                        : 'bg-surface text-amber-100 hover:bg-gray-700'
                    }`}
                  >
                    {w.word}
                  </button>
                ))}
              </div>
              {activeWord && words.some((w) => w.word === activeWord) && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 mx-auto max-w-xl bg-surface rounded-lg p-4 text-left text-sm"
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-amber-100 font-bold uppercase tracking-wide">
                      {activeWord}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveWord(null)}
                      className="text-gray-500 hover:text-gray-300 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                  {!activeDefinition || activeDefinition.status === 'loading' ? (
                    <p className="text-gray-400 italic">Looking it up…</p>
                  ) : activeDefinition.status === 'ok' ? (
                    <ul className="space-y-1">
                      {activeDefinition.entries.map((entry, j) => (
                        <li key={j} className="text-gray-300">
                          {entry.pos && (
                            <span className="text-gray-500 italic mr-2">{entry.pos}</span>
                          )}
                          {entry.def}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-400">
                      No definition found online — it's a valid tournament word (ENABLE list),
                      but too obscure for the dictionary API.
                    </p>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </motion.div>
      )}

      <Link to="/" className="btn btn-secondary mt-2">
        Back to Games
      </Link>
    </div>
  );
};

export default Banagrams;
